<script setup lang="ts">
const { loading = true } = defineProps<{
  loading?: boolean
}>()

const { getCurrentUser } = useAuthStore()
const { resetFilters } = useCollectsStore()
const { user } = storeToRefs(useAuthStore())

const router = useRouter()

const path = computed(() => location.pathname)
const userloading = ref<boolean>(false)
// Add debounce to prevent flickering
const showLoading = ref<boolean>(true)
const isLoading = computed(() => loading || userloading.value)

// Watch for loading state changes with debounce to prevent flickering
watch(isLoading, (newLoading) => {
  if (newLoading) {
    // Immediately show loading when loading starts
    showLoading.value = true
  }
  else {
    // Delay hiding loading by a small amount to prevent flickering
    setTimeout(() => {
      // Only hide if still not loading after timeout
      if (!isLoading.value)
        showLoading.value = false
    }, 300)
  }
}, { immediate: true })

watch(() => authState.state, async () => {
  switch (authState.state) {
    case AuthStatus.SIGN_UP_SUCCESS:
      handleSignupSuccess()
      break
    case AuthStatus.SIGN_IN_SUCCESS:
      handleSigninSuccess()
      break
    case AuthStatus.SIGNED_OUT:
      handleSignout()
      break
  }
}, { immediate: true, deep: true })

async function handleSigninSuccess() {
  // Skip authentication handling for following routes
  if (path.value === '/auth/user-invitation' || path.value.includes('/confirm-collect'))
    return

  // Set loading state immediately to prevent flickering
  userloading.value = true

  try {
    await getCurrentUser()
    await redirectToPath()
  }
  finally {
    // Only turn off loading after everything is complete
    userloading.value = false
  }
}

async function handleSignupSuccess() {
  // Set loading state immediately to prevent flickering
  userloading.value = true

  try {
    await getCurrentUser()
    // Check if we have a specific onboarding path to redirect to
    if (authState.onboardingRedirectPath) {
      const redirectPath = authState.onboardingRedirectPath
      // Reset the path immediately to prevent duplicate redirects
      authState.onboardingRedirectPath = null
      // Navigate to the specified path
      await router.push(redirectPath)
    }
    else {
      await redirectToPath()
    }
  }
  finally {
    // Only turn off loading after everything is complete
    userloading.value = false
  }
}

async function redirectToPath() {
  const queryParams = new URLSearchParams(window.location.search)
  const queryObject = Object.fromEntries(queryParams.entries())

  // Check if we're on the create account page and the state is SSO_SIGN_IN
  // In this case, we want to redirect to home since it's an existing user signing in
  const isCreateAccountPage = path.value.includes('/auth/create-an-account')
  const isLoginPage = path.value === '/auth/login'
  const shouldRedirectToHome = isLoginPage || (isCreateAccountPage && authState.state === AuthStatus.SSO_SIGN_IN)

  const targetPath = shouldRedirectToHome ? '/' : path.value

  // Preserve the current query parameters
  await router.push({
    path: targetPath,
    query: shouldRedirectToHome ? {} : queryObject,
  })
}

async function handleSignout() {
  if (resetFilters)
    resetFilters()
  // @ts-expect-error user is of type CurrentUserGraphql
  user.value = undefined
  token.value = ''
  beforeAuthChangedUser.value = undefined
  useFakeData.value = false
  authState.firebaseUserPopulated = false
  authState.provider = 'firebase'
  authState.initialized = false
}
</script>

<template>
  <div v-if="!showLoading" class="h-full w-full flex flex-col">
    <slot />
  </div>

  <AppSkeleton v-else />
</template>
